import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import config from '../config/env';
import type {
  PaginatedResponse,
  ErrorResponse,
  User,
  Role,
  LoginCredentials,
  LoginResponse,
  CreateUserRequest,
  UpdateUserRequest,
  CreateRoleRequest,
  UpdateRoleRequest,
  UserSearchParams,
  RoleSearchParams,
  RoleUsageInfo,
  BackendRole,
  BackendUser,

  UpdateUserBackendRequest,
  UpdateRoleBackendRequest,
} from '../types';

// API Client class with automatic token refresh and error handling
export class ApiClient {
  private client: AxiosInstance;
  private isRefreshing: boolean = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor(baseURL?: string) {
    const finalBaseURL = baseURL || '/api/v1';
    console.log('API Client baseURL:', finalBaseURL);
    this.client = axios.create({
      baseURL: finalBaseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for handling 401 errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Do not attempt refresh for auth endpoints to avoid loops
        const requestUrl = (originalRequest as any)?.url ?? '';
        const isAuthEndpoint = typeof requestUrl === 'string' && (
          requestUrl.includes('/auth/refresh') ||
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/logout')
        );

        if (error.response?.status === 401 && originalRequest && !isAuthEndpoint) {
          // Delegate refresh logic (will queue concurrent 401s)
          return this.handleTokenRefresh(error);
        }

        // If 401 on refresh or other auth endpoint, force logout
        if (error.response?.status === 401 && isAuthEndpoint) {
          this.clearTokens();
          // Redirect only if refresh failed
          if (requestUrl.includes('/auth/refresh')) {
            window.location.href = '/auth/login';
          }
        }

        return Promise.reject(this.transformError(error));
      }
    );
  }

  private async handleTokenRefresh(error: AxiosError): Promise<AxiosResponse> {
    if (this.isRefreshing) {
      // If already refreshing, wait for the refresh to complete
      return new Promise((resolve) => {
        this.refreshSubscribers.push((token: string) => {
          const originalRequest = error.config!;
          if (!originalRequest.headers) {
            originalRequest.headers = {} as any;
          }
          (originalRequest.headers as any).Authorization = `Bearer ${token}`;
          resolve(this.client(originalRequest));
        });
      });
    }

    this.isRefreshing = true;

    try {
      const response = await this.client.post('/auth/refresh');
      const backendData: any = response.data as any;
      const accessToken: string | undefined = backendData?.access_token;

      if (!accessToken) {
        throw new Error('Refresh response does not contain access_token');
      }

      this.setAccessToken(accessToken);
      
      // Retry all queued requests with new token
      this.refreshSubscribers.forEach(callback => callback(accessToken));
      this.refreshSubscribers = [];
      
      // Retry original request
      const originalRequest = error.config!;
      if (!originalRequest.headers) {
        originalRequest.headers = {} as any;
      }
      (originalRequest.headers as any).Authorization = `Bearer ${accessToken}`;
      return this.client(originalRequest);
    } catch (refreshError) {
      // Refresh failed, redirect to login
      this.clearTokens();
      window.location.href = '/auth/login';
      return Promise.reject(refreshError);
    } finally {
      this.isRefreshing = false;
    }
  }

  private transformError(error: AxiosError): ApiError {
    if (error.response?.data) {
      const errorData = error.response.data as ErrorResponse;
      
      // Handle Pydantic validation errors
      if (Array.isArray(errorData.detail)) {
        const validationErrors = errorData.detail;
        const firstError = validationErrors[0];
        
        if (firstError) {
          // Create a more user-friendly error message
          const fieldName = firstError.loc[firstError.loc.length - 1];
          const message = firstError.msg;
          
          return new ApiError(
            `${fieldName}: ${message}`,
            error.response.status,
            'validation_error',
            error
          );
        }
        
        return new ApiError(
          'Validation error occurred',
          error.response.status,
          'validation_error',
          error
        );
      }
      
      // Handle regular string errors
      return new ApiError(
        typeof errorData.detail === 'string' ? errorData.detail : 'An error occurred',
        error.response.status,
        errorData.errorCode,
        error
      );
    }
    
    if (error.request) {
      return new ApiError('Network error - please check your connection', 0);
    }
    
    return new ApiError(error.message || 'Unknown error occurred', 0);
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private setAccessToken(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // Auth API methods
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    console.log('Making login request to:', this.client.defaults.baseURL + '/auth/login');
    const response = await this.client.post('/auth/login', credentials);
    
    // Backend returns data directly, not wrapped in ApiResponse
    const backendData = response.data;
    
    // Map backend field names to frontend expected names
    const loginResponse: LoginResponse = {
      user: {
        ...backendData.user,
        firstName: backendData.user.first_name,
        lastName: backendData.user.last_name,
        isSuperuser: backendData.user.is_superuser,
        isActive: backendData.user.is_active,
        createdAt: backendData.user.created_at,
        updatedAt: backendData.user.updated_at,
        lastLoginAt: backendData.user.last_login_at,
      },
      accessToken: backendData.access_token,
      refreshToken: backendData.refresh_token || '', // Backend might not return refresh token
    };
    
    this.setAccessToken(loginResponse.accessToken);
    
    return loginResponse;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } finally {
      this.clearTokens();
    }
  }

  // Helper function to transform backend role to frontend format
  private transformBackendRole(backendRole: any): Role {
    return {
      id: backendRole.id,
      name: backendRole.name,
      description: backendRole.description,
      permissions: backendRole.permissions || [],
      serviceName: backendRole.service_name,
      createdAt: backendRole.created_at,
      updatedAt: backendRole.updated_at,
    };
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/auth/me');
    const backendUser = response.data;
    
    // Map backend field names to frontend expected names
    return {
      ...backendUser,
      firstName: backendUser.first_name,
      lastName: backendUser.last_name,
      isSuperuser: backendUser.is_superuser,
      isActive: backendUser.is_active,
      createdAt: backendUser.created_at,
      updatedAt: backendUser.updated_at,
      lastLoginAt: backendUser.last_login_at,
    };
  }

  async refreshToken(): Promise<string> {
    const response = await this.client.post('/auth/refresh');
    const backendData: any = response.data as any;
    const accessToken: string | undefined = backendData?.access_token;
    if (!accessToken) {
      throw new Error('Refresh response does not contain access_token');
    }
    this.setAccessToken(accessToken);
    return accessToken;
  }

  // Users API methods
  async getUsers(params?: UserSearchParams): Promise<PaginatedResponse<User>> {
    const response = await this.client.get<PaginatedResponse<any>>('/users', { params });
    const backendData = response.data;
    
    // Map backend user data to frontend format
    const mappedUsers: User[] = backendData.items.map((backendUser: any) => ({
      id: backendUser.id,
      email: backendUser.email,
      firstName: backendUser.first_name,
      lastName: backendUser.last_name,
      isActive: backendUser.is_active,
      isSuperuser: backendUser.is_superuser,
      roles: (backendUser.roles || []).map(this.transformBackendRole.bind(this)),
      createdAt: backendUser.created_at,
      updatedAt: backendUser.updated_at,
      lastLoginAt: backendUser.last_login_at,
    }));
    
    return {
      items: mappedUsers,
      total: backendData.total,
      page: backendData.page,
      size: backendData.size,
      pages: backendData.pages,
    };
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get<any>(`/users/${id}`);
    const backendUser = response.data;
    
    // Map backend user data to frontend format
    return {
      id: backendUser.id,
      email: backendUser.email,
      firstName: backendUser.first_name,
      lastName: backendUser.last_name,
      isActive: backendUser.is_active,
      isSuperuser: backendUser.is_superuser,
      roles: (backendUser.roles || []).map(this.transformBackendRole.bind(this)),
      createdAt: backendUser.created_at,
      updatedAt: backendUser.updated_at,
      lastLoginAt: backendUser.last_login_at,
    };
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    // Map frontend data to backend format
    const backendData = {
      email: data.email,
      password: data.password,
      first_name: data.firstName,
      last_name: data.lastName,
      is_active: data.isActive,
      role_ids: data.roleIds,
    };
    
    const response = await this.client.post<BackendUser>('/users', backendData);
    const backendUser = response.data;
    
    // Map backend user data to frontend format
    return {
      id: backendUser.id,
      email: backendUser.email,
      firstName: backendUser.first_name,
      lastName: backendUser.last_name,
      isActive: backendUser.is_active,
      isSuperuser: backendUser.is_superuser,
      roles: (backendUser.roles || []).map(this.transformBackendRole.bind(this)),
      createdAt: backendUser.created_at,
      updatedAt: backendUser.updated_at,
      lastLoginAt: backendUser.last_login_at,
    };
  }

  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    // Map frontend data to backend format
    const backendData: UpdateUserBackendRequest = {};
    if (data.email !== undefined) backendData.email = data.email;
    if (data.firstName !== undefined) backendData.first_name = data.firstName;
    if (data.lastName !== undefined) backendData.last_name = data.lastName;
    if (data.isActive !== undefined) backendData.is_active = data.isActive;
    if (data.password !== undefined) backendData.password = data.password;
    
    const response = await this.client.put<BackendUser>(`/users/${id}`, backendData);
    const backendUser = response.data;
    
    // Map backend user data to frontend format
    return {
      id: backendUser.id,
      email: backendUser.email,
      firstName: backendUser.first_name,
      lastName: backendUser.last_name,
      isActive: backendUser.is_active,
      isSuperuser: backendUser.is_superuser,
      roles: (backendUser.roles || []).map(this.transformBackendRole.bind(this)),
      createdAt: backendUser.created_at,
      updatedAt: backendUser.updated_at,
      lastLoginAt: backendUser.last_login_at,
    };
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/users/${id}`);
  }

  async assignRoleToUser(userId: string, roleId: string, serviceName?: string): Promise<void> {
    await this.client.post(`/roles/users/${userId}/roles`, { 
      role_id: roleId, 
      service_name: serviceName 
    });
  }

  async removeRoleFromUser(userId: string, roleId: string, serviceName?: string): Promise<void> {
    const params = serviceName ? { service_name: serviceName } : {};
    await this.client.delete(`/roles/users/${userId}/roles/${roleId}`, { 
      params 
    });
  }

  // Roles API methods
  async getRoles(params?: RoleSearchParams): Promise<PaginatedResponse<Role>> {
    const response = await this.client.get<PaginatedResponse<any>>('/roles', { params });
    const backendData = response.data;
    
    // Map backend role data to frontend format
    const mappedRoles: Role[] = backendData.items.map((backendRole: any) => ({
      id: backendRole.id,
      name: backendRole.name,
      description: backendRole.description,
      permissions: backendRole.permissions || [],
      serviceName: backendRole.service_name,
      createdAt: backendRole.created_at,
      updatedAt: backendRole.updated_at,
    }));
    
    return {
      items: mappedRoles,
      total: backendData.total,
      page: backendData.page,
      size: backendData.size,
      pages: backendData.pages,
    };
  }

  async getRole(id: string): Promise<Role> {
    const response = await this.client.get<BackendRole>(`/roles/${id}`);
    const backendRole = response.data;
    
    // Map backend role data to frontend format
    return {
      id: backendRole.id,
      name: backendRole.name,
      description: backendRole.description,
      permissions: backendRole.permissions || [],
      serviceName: backendRole.service_name,
      createdAt: backendRole.created_at,
      updatedAt: backendRole.updated_at,
    };
  }

  async createRole(data: CreateRoleRequest): Promise<Role> {
    // Map frontend data to backend format
    const backendData = {
      name: data.name,
      description: data.description,
      permissions: data.permissions,
      service_name: data.serviceName,
    };
    
    const response = await this.client.post<BackendRole>('/roles', backendData);
    const backendRole = response.data;
    
    // Map backend role data to frontend format
    return {
      id: backendRole.id,
      name: backendRole.name,
      description: backendRole.description,
      permissions: backendRole.permissions || [],
      serviceName: backendRole.service_name,
      createdAt: backendRole.created_at,
      updatedAt: backendRole.updated_at,
    };
  }

  async updateRole(id: string, data: UpdateRoleRequest): Promise<Role> {
    // Map frontend data to backend format
    const backendData: UpdateRoleBackendRequest = {};
    if (data.name !== undefined) backendData.name = data.name;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.permissions !== undefined) backendData.permissions = data.permissions;
    if (data.serviceName !== undefined) backendData.service_name = data.serviceName;
    
    const response = await this.client.put<BackendRole>(`/roles/${id}`, backendData);
    const backendRole = response.data;
    
    // Map backend role data to frontend format
    return {
      id: backendRole.id,
      name: backendRole.name,
      description: backendRole.description,
      permissions: backendRole.permissions || [],
      serviceName: backendRole.service_name,
      createdAt: backendRole.created_at,
      updatedAt: backendRole.updated_at,
    };
  }

  async deleteRole(id: string): Promise<void> {
    await this.client.delete(`/roles/${id}`);
  }

  async checkRoleUsage(id: string): Promise<RoleUsageInfo> {
    try {
      // First get the role details
      const role = await this.getRole(id);
      
      let allUsersWithRole: User[] = [];
      const pageSize = 100; // Use maximum allowed page size
      let currentPage = 1;
      let hasMorePages = true;
      
      console.log(`Checking role usage for role ${id} (${role.name})`);
      
      while (hasMorePages && currentPage <= 50) { // Safety limit of 50 pages
        try {
          console.log(`Fetching users page ${currentPage}`);
          const usersResponse = await this.getUsers({ 
            page: currentPage, 
            size: pageSize 
          });
          
          console.log(`Page ${currentPage}: Found ${usersResponse.items.length} users, total pages: ${usersResponse.pages}`);
          
          // Filter users that have this role
          const pageUsersWithRole = usersResponse.items.filter(user => {
            const hasRole = user.roles.some(userRole => {
              console.log(`Checking user ${user.email} role ${userRole.id} against target ${id}`);
              return userRole.id === id;
            });
            if (hasRole) {
              console.log(`✓ User ${user.email} has role ${role.name}`);
            }
            return hasRole;
          });
          
          allUsersWithRole.push(...pageUsersWithRole);
          
          // Check if there are more pages
          hasMorePages = currentPage < usersResponse.pages;
          currentPage++;
          
        } catch (pageError) {
          console.error(`Error fetching users page ${currentPage}:`, pageError);
          // If we can't fetch a page, we can't be sure about usage
          // Return conservative result indicating potential usage
          return {
            roleId: id,
            roleName: role.name,
            isInUse: true, // Conservative: assume it's in use if we can't check
            assignedUsersCount: -1, // Unknown count
            assignedUsers: [],
          };
        }
      }
      
      console.log(`Role usage check complete: Found ${allUsersWithRole.length} users with role ${role.name}`);
      
      return {
        roleId: id,
        roleName: role.name,
        isInUse: allUsersWithRole.length > 0,
        assignedUsersCount: allUsersWithRole.length,
        assignedUsers: allUsersWithRole,
      };
      
    } catch (error) {
      console.error('Error in checkRoleUsage:', error);
      throw error;
    }
  }

  // Permissions API methods
  async getAvailablePermissions(): Promise<string[]> {
    const response = await this.client.get<string[]>('/permissions');
    return response.data;
  }
}

// Custom error class for API errors
export class ApiError extends Error {
  public response?: unknown;
  
  constructor(
    message: string,
    public status: number = 0,
    public errorCode?: string,
    originalError?: AxiosError
  ) {
    super(message);
    this.name = 'ApiError';
    this.response = originalError?.response;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidationError(): boolean {
    return this.status === 422;
  }
}

// Error handling utilities
export const handleApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) {
    return error;
  }
  
  if (error instanceof Error) {
    return new ApiError(error.message);
  }
  
  return new ApiError('Unknown error occurred');
};

export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};

// Create singleton instance
export const apiClient = new ApiClient(config.apiUrl);

// Export individual API modules for better organization
export const authApi = {
  login: (credentials: LoginCredentials) => apiClient.login(credentials),
  logout: () => apiClient.logout(),
  getCurrentUser: () => apiClient.getCurrentUser(),
  refreshToken: () => apiClient.refreshToken(),
};

export const userApi = {
  getUsers: (params?: UserSearchParams) => apiClient.getUsers(params),
  getUser: (id: string) => apiClient.getUser(id),
  createUser: (data: CreateUserRequest) => apiClient.createUser(data),
  updateUser: (id: string, data: UpdateUserRequest) => apiClient.updateUser(id, data),
  deleteUser: (id: string) => apiClient.deleteUser(id),
  assignRole: (userId: string, roleId: string, serviceName?: string) => 
    apiClient.assignRoleToUser(userId, roleId, serviceName),
  removeRole: (userId: string, roleId: string, serviceName?: string) => 
    apiClient.removeRoleFromUser(userId, roleId, serviceName),
};

export const roleApi = {
  getRoles: (params?: RoleSearchParams) => apiClient.getRoles(params),
  getRole: (id: string) => apiClient.getRole(id),
  createRole: (data: CreateRoleRequest) => apiClient.createRole(data),
  updateRole: (id: string, data: UpdateRoleRequest) => apiClient.updateRole(id, data),
  deleteRole: (id: string) => apiClient.deleteRole(id),
  checkRoleUsage: (id: string) => apiClient.checkRoleUsage(id),
};

export const permissionApi = {
  getAvailablePermissions: () => apiClient.getAvailablePermissions(),
};
